/**
 * =============================================================================
 * USE TOAST HOOK
 * =============================================================================
 *
 * Hook to access toast functionality from the ToastProvider context.
 *
 * PHASE 1 TASK: P1-7 - Add Toast Notifications for Admin Actions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { showSuccess, showError } = useToast();
 *
 *   const handleApprove = async () => {
 *     try {
 *       await approveQuestion(id);
 *       showSuccess('Question approved successfully');
 *     } catch (error) {
 *       showError('Failed to approve question');
 *     }
 *   };
 *
 *   return <Button onClick={handleApprove}>Approve</Button>;
 * }
 * ```
 *
 * =============================================================================
 */

import { useContext } from 'react';
import { ToastContext } from '../providers/ToastProvider';
import type { ToastContextValue } from '../providers/ToastProvider';

/**
 * Hook to access toast functionality.
 *
 * Returns methods to show success, error, and warning toasts,
 * as well as methods to dismiss toasts.
 *
 * @returns Toast context value with show/dismiss methods
 */
export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}
